import * as turf from "@turf/turf";
import * as d3 from "d3-scale";
import * as chroma from "d3-scale-chromatic";
import * as _ from "lodash";
import React from "react";
import { WebMercatorViewport } from "react-map-gl";
import { toast } from "react-toastify";
import { parse } from "wellknown";
import { GeojsonLayer } from "../ui/geojson-layer";
import { MapMarker } from "../ui/marker";

export const libraryUrl = path =>
  `https://raw.githubusercontent.com/DrewWeth/geodebugger-library/master${path}`;

export const randomBetween = (min, max) => {
  return Math.floor(Math.random() * (max - min + 1) + min);
};

export const copyToClipboard = str => {
  const el = document.createElement("textarea");
  el.value = str;
  el.setAttribute("readonly", "");
  el.style.position = "absolute";
  el.style.left = "-9999px";
  document.body.appendChild(el);
  el.select();
  document.execCommand("copy");
  document.body.removeChild(el);
};

const shortId = layer => `Layer ${layer._id.substring(layer._id.length - 3)}`;

export const getName = layer => {
  switch (layer.type) {
    case "FeatureCollection":
      return _.get(layer, ["properties", "name"]) || shortId(layer);
    default:
      return _.get(layer, ["properties", "name"]) || shortId(layer);
  }
};

export const plotLayer = ({ l, i, metaLookup }) => {
  const color = getLayerColor(metaLookup, l) || "red";
  // console.log({ metaLookup });
  const layerMeta = metaLookup[l._id];
  const key = `${l._id}-${i}`;

  if (!layerMeta.hidden) {
    if (l.type) {
      // If point
      if (l["type"] === "Point") {
        return (
          <MapMarker
            latitude={l.coordinates[1]}
            longitude={l.coordinates[0]}
            mapKey={key}
            color={color}
          />
        );
      } else if (_.get(l, ["type"]) === "Feature") {
        if (_.get(l, ["geometry", "type"]) === "Point") {
          return (
            <MapMarker
              latitude={l.geometry.coordinates[1]}
              longitude={l.geometry.coordinates[0]}
              mapKey={key}
              color={color}
            />
          );
        } else if (_.get(l, ["geometry", "type"]) === "MultiPoint") {
          return _.get(l, ["geometry", "coordinates"]).map((coords, i) => (
            <MapMarker
              latitude={coords[1]}
              longitude={coords[0]}
              mapKey={key}
              color={color}
            />
          ));
        } else {
          return <GeojsonLayer lineColor={color} key={key} layer={l} i={i} />;
        }
      } else {
        return <GeojsonLayer lineColor={color} key={key} layer={l} i={i} />;
      }
    } else {
      return null;
    }
  } else {
    return null;
  }
};

export const centerMapOnLayer = ({ layer, view, setView, useBB = true }) => {
  const point = turf.centroid(layer);
  setView({
    ...view,
    latitude: point.geometry.coordinates[1],
    longitude: point.geometry.coordinates[0]
  });
};

export const sendToast = message => {
  toast(message, { position: toast.POSITION.BOTTOM_RIGHT });
};

export const getBounds = ({ layer, view, padding, showLibrary }) => {
  const [minLng, minLat, maxLng, maxLat] = turf.bbox(layer);
  try {
    const viewport = new WebMercatorViewport(view);
    const padPx = 60;

    const { longitude, latitude, zoom } = viewport.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat]
      ],
      {
        padding: padding
          ? padding
          : {
              left: 500,
              top: padPx,
              right: showLibrary ? 315 : 15,
              bottom: 40
            }
      }
    );
    return { longitude, latitude, zoom };
  } catch (e) {
    console.warn("Error with mercator map", { e });
  }
};

export const getLayerColor = (metaLookup, layer) => {
  return metaLookup[layer._id] ? metaLookup[layer._id].lineColor : "red";
  // return _.get(lookup, [layer._id, "lineColor"]) || "red";
};

export const setLayerColor = (lookup, layer, color) => {};

export const fitBounds = ({ layer, view, setView, padding, showLibrary }) => {
  const { longitude, latitude, zoom } = getBounds({
    layer,
    view,
    padding,
    showLibrary
  });

  if (
    _.get(layer, ["geometry", "type"]) === "Point" ||
    _.get(layer, ["geometry", "type"]) === "MultiPoint"
  ) {
    setView({ ...view, longitude, latitude, zoom: Math.min(10, zoom) });
  } else {
    setView({ ...view, longitude, latitude, zoom });
  }
};

export const partition = (ary, callback) =>
  ary.reduce(
    (acc, e, i) => {
      acc[callback(e, i) ? 0 : 1].push(e);
      return acc;
    },
    [[], []]
  );

export const uniqueId = () =>
  Math.random()
    .toString(36)
    .substring(2, 15) +
  Math.random()
    .toString(36)
    .substring(2, 15);

const coordsPattern = /^(\-?\d+(\.\d+)?),\s*(\-?\d+(\.\d+)?)$/;

const parseNonJson = (text, setDetectedInput) => {
  if (parse(text)) {
    setDetectedInput(["WKT", "green"]);
  } else {
    const lineValidation = text
      .trim()
      .split("\n")
      .map(l => coordsPattern.test(l.trim()));

    if (lineValidation.length > 0 && lineValidation.every(a => a === true)) {
      setDetectedInput(["Coordinates", "green"]);
    }
  }
};

export const determineInputCategory = (text, setDetectedInput) => {
  try {
    const obj = JSON.parse(text);
    if (typeof obj === "object") {
      setDetectedInput(["JSON", "green"]);
    } else {
      parseNonJson(text, setDetectedInput);
    }
  } catch (_) {
    parseNonJson(text, setDetectedInput);
  }
};

export const parseFeatureInput = ({
  addLayer,
  textArea,
  view,
  setView,
  coordsAreXY,
  combinePoints
}) => {
  try {
    addLayer({ layer: JSON.parse(textArea) });
  } catch (e) {
    try {
      const layer = parse(textArea.trim());
      addLayer({ layer });
      fitBounds({ layer, view, setView });
    } catch (ee) {
      // Change to upload array of polygons
      const points = textArea
        .split("\n")
        .map(l => l.split(","))
        .filter(e => e.length > 1)
        .map(strs => {
          let arr = strs.map(e => Number(e.trim()));

          // Do a manual swap because GeoJSON can have 3 points
          // and x and y are represented by the first two.
          if (!coordsAreXY) {
            const temp = arr[0];
            arr[0] = arr[1];
            arr[1] = temp;
          }
          return arr;
        });

      const newPoints = combinePoints
        ? turf.multiPoint(points)
        : points.map(arr => turf.point(arr));

      addLayer({ layer: newPoints });
    }
  }
};

export const saveState = (state, showMessage = false) => {
  const { description, showLibrary, view, ...toSave } = state;
  localStorage.setItem("appState", JSON.stringify(toSave));
  showMessage && sendToast("State saved");
};

export const colorPalette = d3
  .scaleSequential(chroma.interpolateRainbow)
  .domain([0, 1]);

export const setName = (layer, name) => {
  _.set(layer, ["properties", "name"], name);
  return;
};

export function getLayer({ e, addLayer, view, setView }) {
  fetch(e)
    .then(response => response.json())
    .then(layer => {
      const name =
        _.get(layer, ["type"]) === "FeatureCollection"
          ? _.get(layer, ["features", 0, "properties", "name"])
          : _.get(layer, ["properties", "name"]);
      console.log({ name });

      addLayer({ layer, name });
      fitBounds({ layer, view, setView });
    });
}

export const splitFCIntoFeatures = (layer, addLayer) => {
  // _.get(layer, ['features'])
  let newLayers = [];
  turf.flattenEach(layer, (currentFeature, featureIndex, multiFeatureIndex) => {
    let newF = JSON.parse(JSON.stringify(currentFeature));
    // if (_.get(currentFeature, ["properties", "name"])) {
    const newName = `${getName(layer)}-${featureIndex}-${multiFeatureIndex}`;
    _.set(newF, ["properties", "name"], newName);
    // }
    newLayers.push(newF);
  });
  addLayer({ layer: newLayers });
};
